import React from 'react';
import { osc, orbitAngle, wobble, lerp, loop, clamp, pathLerp } from '../../core/AnimationUtils';

/*
 * Concept-acting SCENES — Nature edition (Anivaze v2).
 * Same philosophy as ConceptScenes.tsx: these ACT the concept out instead of
 * labelling a diagram. A 5-year-old should instantly "get it" from the motion.
 * Both scenes are pure functions of `t` — no state, no Date.now(), no random.
 */

/* ━━━ Shared prop interface ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
export interface NatureSceneProps {
  opacity?: number;
  accentColor?: string;
  /** Short friendly caption shown at the bottom of the scene. */
  label?: string;
  /** Seconds since the scene started. */
  t?: number;
}

/* ━━━ Water Cycle ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 * Sun warms the sea → droplets rise (evaporation) → cloud forms & drifts →
 * rain falls on land → river flows back to sea. Total loop ≈ 8 s.
 * ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
export const WaterCycleScene: React.FC<NatureSceneProps> = ({
  opacity = 1,
  accentColor = '#38bdf8',
  t = 0,
  label = 'Water goes round and round!',
}) => {
  /* ── Timing: 8-second main loop ── */
  const CYCLE = 8;
  const phase = loop(t, CYCLE); // 0..1 through the cycle

  /* ── Sun ── */
  const sunPulse = osc(t, 3, 0, 1);
  const sunRayRot = (t * 10) % 360;

  /* ── Evaporation droplets: 6 droplets, staggered across the sea ── */
  // Each droplet rises over ~3s of the 8s cycle, then disappears.
  const dropletCount = 6;
  const seaTop = 290;   // y where sea surface is
  const cloudY = 130;   // where cloud centre lives
  const droplets = Array.from({ length: dropletCount }, (_, i) => {
    // Each droplet has its own phase offset so they stagger naturally.
    const offset = i / dropletCount; // 0..1
    const dp = (phase + offset) % 1; // individual loop phase
    // Rise from seaTop to cloudY; only visible in first 55% of phase.
    const visible = dp < 0.55;
    const riseP = clamp(dp / 0.55, 0, 1);
    const x = 80 + i * 45 + wobble(t, 2.4 + i * 0.3, 5, i);
    const y = lerp(seaTop - 8, cloudY + 20, riseP);
    const alpha = visible ? clamp(1 - Math.pow(riseP - 0.85, 2) / 0.02, 0, 1) : 0;
    return { x, y, alpha };
  });

  /* ── Cloud: swells as droplets arrive, drifts right ── */
  // Cloud grows (puffs up) in phase 0.25..0.60 then shrinks as it rains.
  const cloudGrow = clamp(osc(t, CYCLE * 0.5, 0.7, 1.3), 0.7, 1.3);
  const cloudX = lerp(200, 580, clamp((phase - 0.2) / 0.5, 0, 1));
  const cloudWobble = wobble(t, 3.5, 3);

  /* ── Rain: falls from cloud between phase 0.60 and 0.90 ── */
  const rainPhase = clamp((phase - 0.58) / 0.30, 0, 1);
  const rainVisible = phase > 0.55 && phase < 0.95;
  const rainDropCount = 8;
  const rainDrops = Array.from({ length: rainDropCount }, (_, i) => {
    const col = i % 4;
    const row = Math.floor(i / 4);
    const xBase = cloudX - 60 + col * 38;
    const dropPhase = loop(t * rainPhase * 2.5 + i * 0.17, 1);
    const yDrop = lerp(cloudY + 40, 310, dropPhase);
    return { x: xBase + wobble(t, 1.2, 2, i), y: yDrop + row * 8, alpha: rainVisible ? rainPhase * 0.85 : 0 };
  });

  /* ── River: flows from foot of land back to sea ── */
  // River path: left-to-right across the lower portion of the scene.
  const riverPath = [
    { x: 580, y: 330 },
    { x: 520, y: 340 },
    { x: 440, y: 345 },
    { x: 360, y: 350 },
    { x: 260, y: 348 },
    { x: 180, y: 344 },
    { x: 100, y: 340 },
  ];
  // Flow ripple particles along the river.
  const riverParticleCount = 5;
  const riverParticles = Array.from({ length: riverParticleCount }, (_, i) => {
    const p = loop(t * 0.14 + i / riverParticleCount, 1);
    return pathLerp(riverPath, p);
  });

  /* ── Arrow helpers (curved upward arc for "evaporation" concept) ── */
  // Small arrow indicating upward cycle direction — drawn near sea.
  const arrowOpacity = clamp(osc(t, 2, 0.2, 0.8), 0, 1);

  return (
    <g opacity={opacity}>
      <defs>
        <linearGradient id="wc-sky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#bae6fd" />
          <stop offset="60%" stopColor="#e0f2fe" />
          <stop offset="100%" stopColor="#f0fdf4" />
        </linearGradient>
        <linearGradient id="wc-sea" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={accentColor} />
          <stop offset="100%" stopColor="#0369a1" />
        </linearGradient>
        <radialGradient id="wc-sun" cx="50%" cy="50%">
          <stop offset="0%" stopColor="#fff7cc" />
          <stop offset="70%" stopColor="#fbbf24" />
          <stop offset="100%" stopColor="#f59e0b" />
        </radialGradient>
        <linearGradient id="wc-land" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#4ade80" />
          <stop offset="100%" stopColor="#16a34a" />
        </linearGradient>
        <filter id="wc-blur" x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="3" />
        </filter>
      </defs>

      {/* Sky */}
      <rect x={0} y={0} width={800} height={450} fill="url(#wc-sky)" />

      {/* ── Sun ── */}
      <g transform="translate(700 85)">
        <g transform={`rotate(${sunRayRot})`} opacity={0.9}>
          {Array.from({ length: 12 }, (_, k) => {
            const a = (k / 12) * Math.PI * 2;
            const r1 = 40 + sunPulse * 3;
            const r2 = 56 + sunPulse * 5;
            return (
              <line key={k}
                x1={r1 * Math.cos(a)} y1={r1 * Math.sin(a)}
                x2={r2 * Math.cos(a)} y2={r2 * Math.sin(a)}
                stroke="#fcd34d" strokeWidth={3} strokeLinecap="round"
              />
            );
          })}
        </g>
        <circle r={32 + sunPulse * 1.5} fill="url(#wc-sun)" />
        {/* Cute sun smile */}
        <path d="M-10,10 Q0,18 10,10" fill="none" stroke="#b45309" strokeWidth={2.5} strokeLinecap="round" />
        <circle cx={-8} cy={2} r={3} fill="#92400e" />
        <circle cx={8} cy={2} r={3} fill="#92400e" />
      </g>

      {/* Wavy heat shimmer lines from sun toward sea */}
      {Array.from({ length: 3 }, (_, k) => {
        const shX = 630 + k * 22;
        const shOff = wobble(t, 0.8 + k * 0.2, 4, k);
        return (
          <path key={k}
            d={`M${shX + shOff} 130 Q${shX + shOff + 6} 160 ${shX + shOff} 190 Q${shX + shOff - 6} 220 ${shX + shOff} 250`}
            fill="none" stroke="#fde68a" strokeWidth={2} strokeLinecap="round" opacity={0.45}
          />
        );
      })}

      {/* ── Sea ── */}
      <rect x={0} y={seaTop} width={310} height={160} fill="url(#wc-sea)" rx={0} />
      {/* Sea wave surface */}
      <path
        d={`M0,${seaTop} Q${40 + wobble(t, 1.4, 6)},${seaTop - 10} ${80},${seaTop} Q${120 + wobble(t, 1.2, 8, 1)},${seaTop + 10} ${160},${seaTop} Q${200 + wobble(t, 1.6, 7, 2)},${seaTop - 8} ${240},${seaTop} Q${275},${seaTop + 6} ${310},${seaTop}`}
        fill={accentColor} opacity={0.7} strokeWidth={0}
      />
      {/* Sea label */}
      <text x={150} y={seaTop + 60} textAnchor="middle" fill="#fff" fontSize={15} fontWeight={700} fontFamily="Inter,sans-serif" opacity={0.9}>Sea</text>

      {/* ── Green land ── */}
      <ellipse cx={560} cy={370} rx={230} ry={90} fill="url(#wc-land)" />
      <ellipse cx={560} cy={355} rx={220} ry={75} fill="#4ade80" />
      {/* Little trees on land */}
      {[490, 530, 575, 620].map((tx, i) => (
        <g key={i} transform={`translate(${tx} ${345 - i % 2 * 8})`}>
          <rect x={-3} y={0} width={6} height={14} rx={2} fill="#92400e" />
          <ellipse cx={0} cy={-10} rx={12} ry={14} fill="#16a34a" />
          <ellipse cx={0} cy={-18} rx={8} ry={10} fill="#22c55e" />
        </g>
      ))}

      {/* ── Evaporation droplets ── */}
      {droplets.map((d, i) => (
        <g key={i} transform={`translate(${d.x} ${d.y})`} opacity={d.alpha}>
          {/* Teardrop shape: small circle with tail */}
          <ellipse cx={0} cy={0} rx={5} ry={7} fill={accentColor} />
          <path d="M-3,4 Q0,12 3,4" fill={accentColor} />
        </g>
      ))}

      {/* Evaporation upward-arrow hint */}
      <g opacity={arrowOpacity}>
        <path d="M155 270 Q162 240 155 210" fill="none" stroke="#0ea5e9" strokeWidth={2.5} strokeLinecap="round" strokeDasharray="4 4" />
        <polygon points="151,214 155,200 159,214" fill="#0ea5e9" />
        <text x={165} y={240} fill="#0369a1" fontSize={11} fontWeight={600} fontFamily="Inter,sans-serif">rises!</text>
      </g>

      {/* ── Cloud ── */}
      <g transform={`translate(${cloudX} ${cloudY + cloudWobble}) scale(${cloudGrow})`} opacity={clamp(phase * 4, 0.1, 1)}>
        {/* Shadow under cloud */}
        <ellipse cx={0} cy={42} rx={72} ry={8} fill="rgba(0,0,0,0.08)" filter="url(#wc-blur)" />
        {/* Fluffy cloud puffs */}
        <ellipse cx={0} cy={0} rx={54} ry={34} fill="#fff" />
        <ellipse cx={50} cy={10} rx={38} ry={28} fill="#fff" />
        <ellipse cx={-46} cy={12} rx={34} ry={24} fill="#fff" />
        <ellipse cx={18} cy={-18} rx={30} ry={22} fill="#f0f9ff" />
        <ellipse cx={-20} cy={-12} rx={28} ry={20} fill="#e0f2fe" />
        {/* Cloud has a cute face */}
        <circle cx={-10} cy={6} r={3.5} fill="#93c5fd" />
        <circle cx={10} cy={6} r={3.5} fill="#93c5fd" />
        <path d="M-8,16 Q0,22 8,16" fill="none" stroke="#93c5fd" strokeWidth={2.5} strokeLinecap="round" />
      </g>

      {/* ── Rain drops ── */}
      {rainDrops.map((rd, i) => (
        <g key={i} transform={`translate(${rd.x} ${rd.y})`} opacity={rd.alpha}>
          <ellipse cx={0} cy={0} rx={3} ry={5} fill="#38bdf8" />
          <path d="M-2,3 Q0,9 2,3" fill="#38bdf8" />
        </g>
      ))}

      {/* Rain label when raining */}
      {rainVisible && (
        <text x={cloudX + 10} y={cloudY + 75} textAnchor="middle" fill="#0369a1" fontSize={12} fontWeight={700} fontFamily="Inter,sans-serif" opacity={rainPhase}>Rain!</text>
      )}

      {/* ── River ── */}
      {/* River bed */}
      <path
        d="M580,330 Q520,340 440,345 Q360,350 260,348 Q180,344 100,340"
        fill="none" stroke="#7dd3fc" strokeWidth={9} strokeLinecap="round" opacity={0.6}
      />
      <path
        d="M580,330 Q520,340 440,345 Q360,350 260,348 Q180,344 100,340"
        fill="none" stroke={accentColor} strokeWidth={5} strokeLinecap="round" opacity={0.8}
      />
      {/* River flow label */}
      <text x={340} y={368} textAnchor="middle" fill="#0369a1" fontSize={11} fontWeight={600} fontFamily="Inter,sans-serif">river flows back</text>
      {/* Flow particles */}
      {riverParticles.map((rp, i) => (
        <ellipse key={i} cx={rp.x} cy={rp.y} rx={4} ry={2.5} fill="#fff" opacity={0.75} />
      ))}

      {/* Cycle direction arrow — curved arc from river mouth back to sea */}
      <path
        d="M108 338 Q70 360 60 340 Q48 310 72 295"
        fill="none" stroke="#0ea5e9" strokeWidth={2.5} strokeLinecap="round" opacity={0.5}
      />
      <polygon points="69,290 72,305 78,293" fill="#0ea5e9" opacity={0.5} />

      {/* ── Caption ── */}
      <g transform="translate(400 425)">
        <rect x={-210} y={-22} width={420} height={40} rx={20} fill="rgba(255,255,255,0.88)" stroke="#bae6fd" strokeWidth={2} />
        <text x={0} y={4} textAnchor="middle" fill="#0f172a" fontSize={16} fontWeight={700} fontFamily="Inter,sans-serif">{label}</text>
      </g>
    </g>
  );
};

/* ━━━ Day / Night Scene ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 * Earth rotates in the centre. Sun on the left bathing that side in warm
 * yellow; Moon + stars on the right in cool blue-black. A tiny house on the
 * globe has a glowing window by day and a dark window with a moon symbol by
 * night. Sky gradient around the whole scene shifts day↔night. Loop ≈ 8 s.
 * ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
export const DayNightScene: React.FC<NatureSceneProps> = ({
  opacity = 1,
  accentColor = '#fbbf24',
  t = 0,
  label = 'Earth spins to make day and night!',
}) => {
  const CYCLE = 8; // one full day+night per 8 s
  const spinAngle = orbitAngle(t, CYCLE); // radians, full revolution

  /* ── Determine "day fraction" for sky tint: 0=night, 1=full day ── */
  // House is at the top of the globe initially. dayFraction = cos(spinAngle).
  // Positive cos → facing sun (left side) → day. Range mapped to [0,1].
  const dayFraction = clamp((Math.cos(spinAngle) + 1) / 2, 0, 1);

  // Sky gradient endpoints interpolated day ↔ night.
  const skyTopDay = '#bae6fd';
  const skyTopNight = '#0f172a';
  const skyBotDay = '#fde68a';
  const skyBotNight = '#1e3a5f';
  // Simple hex lerp via rgba strings — use opacity layering instead.
  const nightOverlay = 1 - dayFraction; // more night → more dark overlay

  /* ── Sun ── */
  const sunPulse = osc(t, 3, 0, 1);
  const sunRayRot = (t * 8) % 360;

  /* ── Moon gentle bob ── */
  const moonBob = wobble(t, 4, 5);

  /* ── Stars twinkle ── */
  const starPositions = [
    { x: 640, y: 55 }, { x: 680, y: 95 }, { x: 710, y: 45 }, { x: 730, y: 120 },
    { x: 755, y: 70 }, { x: 670, y: 135 }, { x: 760, y: 160 }, { x: 625, y: 110 },
    { x: 695, y: 180 }, { x: 745, y: 195 }, { x: 615, y: 170 }, { x: 780, y: 105 },
  ];

  /* ── Globe radius & position ── */
  const globeR = 115;
  const globeCX = 400;
  const globeCY = 220;

  /* ── Continent blobs (rotated with the globe) ── */
  const spinDeg = (spinAngle * 180) / Math.PI;

  /* ── House on the globe surface (sits at the top, rotates with globe) ── */
  // House anchor is at angle = spinAngle (from top, clockwise).
  // We place the house always at the "top-ish" of the globe but let the globe
  // rotate underneath — simpler and more readable. The house itself doesn't
  // move; the continent texture rotates beneath it, implying the spin.
  // Position: top of globe.
  const houseX = globeCX;
  const houseY = globeCY - globeR - 8;
  const windowGlow = dayFraction;   // 1 = bright yellow window; 0 = dark
  const windowColor = dayFraction > 0.5 ? '#fde68a' : '#1e3a5f';

  /* ── Orbit indicator dots (show the path Earth "spins") ── */
  const indicatorAngle = orbitAngle(t, CYCLE * 0.5); // faster for decoration

  return (
    <g opacity={opacity}>
      <defs>
        {/* Day sky */}
        <linearGradient id="dn-sky-day" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={skyTopDay} />
          <stop offset="100%" stopColor={skyBotDay} />
        </linearGradient>
        {/* Night sky */}
        <linearGradient id="dn-sky-night" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={skyTopNight} />
          <stop offset="100%" stopColor={skyBotNight} />
        </linearGradient>
        {/* Globe ocean */}
        <radialGradient id="dn-ocean" cx="40%" cy="35%">
          <stop offset="0%" stopColor="#60a5fa" />
          <stop offset="60%" stopColor="#1d4ed8" />
          <stop offset="100%" stopColor="#1e3a8a" />
        </radialGradient>
        {/* Globe sun-side highlight */}
        <radialGradient id="dn-daylight" cx="30%" cy="35%">
          <stop offset="0%" stopColor="rgba(255,253,200,0.55)" />
          <stop offset="60%" stopColor="rgba(255,253,200,0)" />
        </radialGradient>
        {/* Globe night shadow */}
        <radialGradient id="dn-shadow" cx="75%" cy="50%">
          <stop offset="0%" stopColor="rgba(5,10,30,0.80)" />
          <stop offset="55%" stopColor="rgba(5,10,30,0)" />
        </radialGradient>
        {/* Sun radial */}
        <radialGradient id="dn-sun" cx="50%" cy="50%">
          <stop offset="0%" stopColor="#fff7cc" />
          <stop offset="70%" stopColor={accentColor} />
          <stop offset="100%" stopColor="#f59e0b" />
        </radialGradient>
        <filter id="dn-glow" x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur stdDeviation="6" result="blur" />
          <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
        <clipPath id="dn-globe-clip">
          <circle cx={globeCX} cy={globeCY} r={globeR} />
        </clipPath>
      </defs>

      {/* ── Background ── */}
      {/* Day layer */}
      <rect x={0} y={0} width={800} height={450} fill="url(#dn-sky-day)" />
      {/* Night overlay fades in */}
      <rect x={0} y={0} width={800} height={450} fill="url(#dn-sky-night)" opacity={nightOverlay} />

      {/* ── Stars (right side, visible at night) ── */}
      {starPositions.map((s, i) => {
        const twinkle = osc(t, 1.2 + i * 0.18, 0.2, 1, i * 0.9);
        return (
          <circle key={i} cx={s.x} cy={s.y} r={2 + (i % 3) * 0.8}
            fill="#f8fafc" opacity={twinkle * nightOverlay}
          />
        );
      })}

      {/* ── Sun (left side) ── */}
      <g transform="translate(110 140)">
        <g transform={`rotate(${sunRayRot})`} opacity={dayFraction * 0.9 + 0.1}>
          {Array.from({ length: 12 }, (_, k) => {
            const a = (k / 12) * Math.PI * 2;
            const r1 = 46 + sunPulse * 3;
            const r2 = 64 + sunPulse * 6;
            return (
              <line key={k}
                x1={r1 * Math.cos(a)} y1={r1 * Math.sin(a)}
                x2={r2 * Math.cos(a)} y2={r2 * Math.sin(a)}
                stroke="#fcd34d" strokeWidth={3} strokeLinecap="round"
              />
            );
          })}
        </g>
        <circle r={36 + sunPulse * 2} fill="url(#dn-sun)" opacity={dayFraction * 0.6 + 0.4} />
        {/* Sun face */}
        <path d="M-12,12 Q0,20 12,12" fill="none" stroke="#b45309" strokeWidth={2.5} strokeLinecap="round" />
        <circle cx={-9} cy={4} r={3.5} fill="#92400e" />
        <circle cx={9} cy={4} r={3.5} fill="#92400e" />
        <text x={0} y={58} textAnchor="middle" fill="#92400e" fontSize={12} fontWeight={700} fontFamily="Inter,sans-serif" opacity={dayFraction * 0.8 + 0.2}>Sun</text>
      </g>

      {/* Sun beam highlight toward globe */}
      <path
        d={`M148 140 Q270 180 ${globeCX - globeR} ${globeCY}`}
        fill="none" stroke="#fde68a" strokeWidth={18} strokeLinecap="round" opacity={dayFraction * 0.18}
      />

      {/* ── Moon (right side) ── */}
      <g transform={`translate(690 ${140 + moonBob})`}>
        {/* Crescent: full circle minus clip */}
        <circle r={28} fill="#e2e8f0" opacity={nightOverlay * 0.7 + 0.3} />
        <circle cx={10} cy={-5} r={24} fill={lerp(14, 200, nightOverlay) < 100 ? '#1e3a5f' : '#0f172a'} opacity={nightOverlay} />
        <text x={0} y={48} textAnchor="middle" fill="#94a3b8" fontSize={12} fontWeight={700} fontFamily="Inter,sans-serif" opacity={nightOverlay}>Moon</text>
      </g>

      {/* ── Earth Globe ── */}
      {/* Ocean base */}
      <circle cx={globeCX} cy={globeCY} r={globeR} fill="url(#dn-ocean)" />

      {/* Continent blobs — rotate with the globe */}
      <g clipPath="url(#dn-globe-clip)">
        <g transform={`rotate(${spinDeg} ${globeCX} ${globeCY})`}>
          {/* "Americas"-ish blob */}
          <ellipse cx={globeCX - 50} cy={globeCY - 10} rx={30} ry={48} fill="#4ade80" opacity={0.85} transform={`rotate(-15 ${globeCX - 50} ${globeCY - 10})`} />
          <ellipse cx={globeCX - 55} cy={globeCY + 50} rx={22} ry={30} fill="#22c55e" opacity={0.8} />
          {/* "Europe/Africa"-ish blob */}
          <ellipse cx={globeCX + 30} cy={globeCY - 20} rx={22} ry={35} fill="#86efac" opacity={0.8} transform={`rotate(10 ${globeCX + 30} ${globeCY - 20})`} />
          <ellipse cx={globeCX + 35} cy={globeCY + 30} rx={18} ry={28} fill="#4ade80" opacity={0.75} />
          {/* "Asia" blob */}
          <ellipse cx={globeCX + 70} cy={globeCY - 25} rx={34} ry={28} fill="#86efac" opacity={0.7} transform={`rotate(-8 ${globeCX + 70} ${globeCY - 25})`} />
          {/* Ice caps */}
          <ellipse cx={globeCX} cy={globeCY - globeR + 12} rx={38} ry={14} fill="#f0f9ff" opacity={0.9} />
          <ellipse cx={globeCX} cy={globeCY + globeR - 12} rx={32} ry={12} fill="#e0f2fe" opacity={0.85} />
        </g>

        {/* Daylight highlight on globe face */}
        <circle cx={globeCX} cy={globeCY} r={globeR} fill="url(#dn-daylight)" />

        {/* Night shadow on the opposite side */}
        <circle cx={globeCX} cy={globeCY} r={globeR} fill="url(#dn-shadow)" />
      </g>

      {/* Globe outline */}
      <circle cx={globeCX} cy={globeCY} r={globeR} fill="none" stroke="#1d4ed8" strokeWidth={3} opacity={0.5} />

      {/* Globe atmosphere glow ring */}
      <circle cx={globeCX} cy={globeCY} r={globeR + 8} fill="none" stroke="#7dd3fc" strokeWidth={4} opacity={0.25} />

      {/* ── Latitude lines (subtle structure) ── */}
      {[-55, -25, 0, 25, 55].map((latY, i) => {
        const halfW = Math.sqrt(Math.max(0, globeR * globeR - latY * latY));
        return (
          <ellipse key={i} cx={globeCX} cy={globeCY + latY} rx={halfW} ry={halfW * 0.25}
            fill="none" stroke="#93c5fd" strokeWidth={1} opacity={0.25}
            clipPath="url(#dn-globe-clip)"
          />
        );
      })}

      {/* ── House on globe (top of sphere) ── */}
      <g transform={`translate(${houseX} ${houseY})`}>
        {/* House body */}
        <rect x={-14} y={-16} width={28} height={22} rx={3} fill="#fef3c7" stroke="#d97706" strokeWidth={1.5} />
        {/* Roof */}
        <polygon points="-18,-16 0,-36 18,-16" fill="#ef4444" stroke="#b91c1c" strokeWidth={1.5} />
        {/* Door */}
        <rect x={-5} y={-4} width={10} height={10} rx={2} fill="#92400e" />
        {/* Window – glows yellow by day, dark blue by night */}
        <rect x={-12} y={-13} width={10} height={9} rx={2} fill={windowColor} opacity={0.9} stroke="#d97706" strokeWidth={1} />
        {/* Window glow halo at day */}
        <rect x={-13} y={-14} width={12} height={11} rx={3} fill="#fde68a" opacity={windowGlow * 0.4} filter="url(#dn-glow)" />
        {/* Moon symbol in window at night */}
        {dayFraction < 0.35 && (
          <text x={-7} y={-7} fill="#e2e8f0" fontSize={7} fontWeight={700} fontFamily="Inter,sans-serif" opacity={1 - dayFraction * 2.5}>☽</text>
        )}
        {/* Smoke from chimney */}
        <circle cx={10} cy={-40 + wobble(t, 1.5, 4)} r={3.5} fill="#e2e8f0" opacity={0.5} />
        <circle cx={12 + wobble(t, 1.2, 3, 1)} cy={-52 + wobble(t, 1.8, 4, 1)} r={2.5} fill="#e2e8f0" opacity={0.3} />
      </g>

      {/* ── Day / Night label on each side ── */}
      <g opacity={dayFraction}>
        <text x={200} y={50} textAnchor="middle" fill="#f59e0b" fontSize={18} fontWeight={800} fontFamily="Inter,sans-serif">DAY ☀</text>
      </g>
      <g opacity={nightOverlay}>
        <text x={610} y={50} textAnchor="middle" fill="#e2e8f0" fontSize={18} fontWeight={800} fontFamily="Inter,sans-serif">NIGHT ★</text>
      </g>

      {/* ── Orbit path indicator (subtle ring around Earth) ── */}
      <ellipse cx={globeCX} cy={globeCY} rx={globeR + 35} ry={18}
        fill="none" stroke="#64748b" strokeWidth={1.5} strokeDasharray="6 5" opacity={0.35}
      />
      {/* Little indicator dot on orbit path */}
      <circle
        cx={globeCX + (globeR + 35) * Math.cos(indicatorAngle)}
        cy={globeCY + 18 * Math.sin(indicatorAngle)}
        r={4} fill="#94a3b8" opacity={0.5}
      />

      {/* ── Caption ── */}
      <g transform="translate(400 425)">
        <rect x={-240} y={-22} width={480} height={40} rx={20} fill="rgba(255,255,255,0.88)" stroke="#bae6fd" strokeWidth={2} />
        <text x={0} y={4} textAnchor="middle" fill="#0f172a" fontSize={16} fontWeight={700} fontFamily="Inter,sans-serif">{label}</text>
      </g>
    </g>
  );
};
